// Audio Preview Player with Waveform
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Play, Pause, Volume2, VolumeX, Download, X } from 'lucide-react';
import { AudioFile } from '@types';
import { useFileStore } from '@stores/useFileStore';
import { useAudioStore } from '@stores/useAudioStore';
import { audioService } from '@services/audio.service';
import { formatTime } from '@utils/format';
import { WaveformLoader } from './WaveformLoader';

function isAbortError(error: unknown): boolean {
  if (error instanceof Error) {
    return error.name === 'AbortError' || error.message?.toLowerCase().includes('aborted');
  }
  if (typeof error === 'object' && error !== null && 'name' in error) {
    return (error as { name?: string }).name === 'AbortError';
  }
  return false;
}


interface AudioPreviewProps {
  file: AudioFile;
  onClose?: () => void;
}

export const AudioPreview: React.FC<AudioPreviewProps> = ({ file, onClose }) => {
  const waveformRef = useRef<HTMLDivElement>(null);
  const pendingPlayRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const loadedFileIdRef = useRef<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(file.duration || 0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [wavesurfer, setWavesurfer] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isWaveformReady, setIsWaveformReady] = useState(false);
  const { getBlobUrl } = useFileStore();
  const { stop } = useAudioStore();

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  
  // Stop miniplayer when preview opens
  useEffect(() => {
    stop();
  }, [stop]);

  useEffect(() => {
    const loadAudio = async () => {
      if (loadedFileIdRef.current === file.id && audioUrl) {
        return;
      }
      setIsLoading(true);
      setIsWaveformReady(false);
      try {
        console.log('🎵 Loading audio for:', file.name);
        console.log('📊 File data:', {
          id: file.id,
          url: file.url,
          cdn_url: file.cdn_url,
          s3_key: file.s3_key,
        });
        
        let url = await getBlobUrl(file.id);
        console.log('📥 Got URL:', url);
        
        if (url) {
          // Normalize URL: ensure it starts with https://
          if (!url.startsWith('http://') && !url.startsWith('https://')) {
            url = `https://${url}`;
          }
          
          // Validate URL format
          try {
            new URL(url);
            console.log('✅ Normalized and validated URL:', url.substring(0, 100) + '...');
            loadedFileIdRef.current = file.id;
            setAudioUrl(url);
          } catch (urlError) {
            console.error('❌ Invalid URL format:', url);
            console.error('❌ URL error:', urlError);
            setIsLoading(false);
            if (onCloseRef.current) {
              setTimeout(() => {
                alert('Invalid audio URL. Please try again.');
                onCloseRef.current?.();
              }, 100);
            }
          }
        } else {
          console.error('❌ No URL returned for:', file.name);
          setIsLoading(false);
          if (onCloseRef.current) {
            setTimeout(() => {
              alert('Could not get audio URL. Please try again.');
              onCloseRef.current?.();
            }, 100);
          }
        }
      } catch (error) {
        console.error('❌ Error loading audio:', error);
        setIsLoading(false);
        if (onCloseRef.current) {
          setTimeout(() => onCloseRef.current?.(), 100);
        }
      }
    };
    loadAudio();
  }, [file.id, getBlobUrl, audioUrl, file.name]);

  // Initialize WaveSurfer
  useEffect(() => {
    if (!waveformRef.current || !audioUrl) return;

    const ws = audioService.createWaveSurfer(waveformRef.current, {
      height: 120,
      barWidth: 3,
      barGap: 2,
      barRadius: 3,
      waveColor: 'rgba(48, 216, 112, 0.3)',
      progressColor: 'rgba(48, 216, 112, 0.8)',
      cursorColor: '#00ff61',
      cursorWidth: 2,
    });

    // Add error handler before loading (ignore AbortError when user closes panel during load)
    ws.on('error', (error: unknown) => {
      if (isAbortError(error)) return;
      console.error('❌ WaveSurfer error:', error);
      console.error('❌ Audio URL:', audioUrl);
      setIsLoading(false);
      setIsWaveformReady(false);
      if (error instanceof Error && error.message) {
        console.error('❌ Error message:', error.message);
      }
      if (onCloseRef.current) {
        setTimeout(() => {
          alert('Failed to load audio. Please try again or check if the file is accessible.');
          onCloseRef.current?.();
        }, 100);
      }
    });

    try {
      ws.load(audioUrl).catch((err: unknown) => {
        if (!isAbortError(err)) console.error('❌ Failed to load audio URL:', err);
      });
    } catch (error) {
      console.error('❌ Failed to load audio URL:', error);
      console.error('❌ URL:', audioUrl);
      setIsLoading(false);
      setIsWaveformReady(false);
      if (onCloseRef.current) {
        setTimeout(() => {
          alert('Failed to load audio. The URL may be invalid or expired.');
          onCloseRef.current?.();
        }, 100);
      }
      return;
    }

    ws.on('ready', () => {
      setDuration(ws.getDuration());
      setIsWaveformReady(true);
      setIsLoading(false);
      if (pendingPlayRef.current) {
        pendingPlayRef.current = false;
        ws.play();
      }
    });

    ws.on('audioprocess', () => {
      setCurrentTime(ws.getCurrentTime());
    });

    ws.on('finish', () => {
      setIsPlaying(false);
    });

    ws.on('play', () => setIsPlaying(true));
    ws.on('pause', () => setIsPlaying(false));

    setWavesurfer(ws);

    return () => {
      ws.destroy();
      if (audioUrl && audioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const togglePlayPause = () => {
    if (wavesurfer) {
      if (!isWaveformReady) {
        pendingPlayRef.current = !isPlaying;
        if (!pendingPlayRef.current) {
          setIsPlaying(false);
        }
        return;
      }
      wavesurfer.playPause();
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (wavesurfer) {
      wavesurfer.setVolume(newVolume);
    }
    if (newVolume > 0) {
      setIsMuted(false);
    }
  };

  const toggleMute = () => {
    if (wavesurfer) {
      if (isMuted) {
        wavesurfer.setVolume(volume);
        setIsMuted(false);
      } else {
        wavesurfer.setVolume(0);
        setIsMuted(true);
      }
    }
  };

  const handleDownload = async () => {
    if (!audioUrl) {
      console.error('No audio URL available for download');
      return;
    }
    
    try {
      console.log('📥 Downloading:', file.originalName);
      
      // Fetch the file as blob
      const response = await fetch(audioUrl);
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const a = document.createElement('a');
      a.href = url;
      a.download = file.originalName;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Download started');
    } catch (error) {
      console.error('❌ Download failed:', error);
      // Fallback: try direct link
      window.open(audioUrl, '_blank');
    }
  };

  return createPortal(
    <>
      {/* Full-screen backdrop that covers everything including header */}
      <div 
        className="fixed z-[9998] bg-black/90 backdrop-blur-sm" 
        style={{ 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0,
          width: '100vw',
          height: '100vh',
          position: 'fixed',
          margin: 0,
          padding: 0
        }} 
        onClick={onClose}
      />
      {/* Dialog container */}
      <div 
        className="fixed z-[9999] flex items-center justify-center p-2 sm:p-3" 
        style={{ 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0,
          width: '100vw',
          height: '100vh',
          position: 'fixed',
          margin: 0,
          pointerEvents: 'none'
        }}
      >
        <div 
          className="w-full max-w-3xl sm:max-w-4xl bg-dark-secondary border border-white/10 rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[95vh] overflow-y-auto" 
          onClick={(e) => e.stopPropagation()}
          style={{ pointerEvents: 'auto' }}
        >
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-white/10 sticky top-0 bg-dark-secondary z-10">
          <div className="flex-1 min-w-0 pr-2">
            <h2 className="font-dela text-base sm:text-lg text-white mb-0.5 truncate">{file.name}</h2>
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-xs text-white/60">
              <span className="font-inter font-bold">{file.type.toUpperCase()}</span>
              {file.bpm && <span>• {file.bpm} BPM</span>}
              {file.key && <span>• {file.key}</span>}
              {file.collaborator?.trim() && <span>· with {file.collaborator.trim()}</span>}
              <span className="hidden sm:inline">• {formatTime(duration)}</span>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="flex-shrink-0 p-1.5 sm:p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          )}
        </div>

        {/* Waveform */}
        <div className="bg-dark/50 relative" style={{ minHeight: '120px' }}>
          {isLoading || !isWaveformReady ? (
            <WaveformLoader height={120} />
          ) : null}
          <div 
            ref={waveformRef} 
            className="w-full" 
            style={{ 
              visibility: isLoading || !isWaveformReady ? 'hidden' : 'visible',
              position: isLoading || !isWaveformReady ? 'absolute' : 'relative',
              top: isLoading || !isWaveformReady ? 0 : 'auto',
              left: isLoading || !isWaveformReady ? 0 : 'auto',
              width: '100%',
              minHeight: '120px'
            }} 
          />
        </div>

        {/* Controls */}
        <div className="p-3 sm:p-4 bg-dark-secondary border-t border-white/10">
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
            {/* Play/Pause Button */}
            <button
              onClick={togglePlayPause}
              className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-gradient-to-r from-[#30d870]/80 to-[#00ff61]/80 hover:from-[#30d870] hover:to-[#00ff61] rounded-md text-black transition-all hover:scale-105 shadow-lg shadow-primary/20"
            >
              {isPlaying ? (
                <Pause className="w-4 h-4 sm:w-5 sm:h-5 fill-current" />
              ) : (
                <Play className="w-4 h-4 sm:w-5 sm:h-5 fill-current ml-0.5 sm:ml-1" />
              )}
            </button>

            {/* Time Display */}
            <div className="flex items-center gap-1.5 sm:gap-2 font-codec text-white text-xs sm:text-sm">
              <span className="text-primary font-bold">{formatTime(currentTime)}</span>
              <span className="text-white/40">/</span>
              <span className="text-white/60">{formatTime(duration)}</span>
            </div>

            {/* Volume Control - Hidden on mobile */}
            <div className="hidden sm:flex items-center gap-3 ml-auto">
              <button
                onClick={toggleMute}
                className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all"
              >
                {isMuted || volume === 0 ? (
                  <VolumeX className="w-5 h-5" />
                ) : (
                  <Volume2 className="w-5 h-5" />
                )}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="w-24 h-1 bg-white/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:cursor-pointer"
              />
            </div>

            {/* Download Button */}
            <button
              onClick={handleDownload}
              className="w-full sm:w-auto p-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all flex items-center justify-center gap-2"
            >
              <Download className="w-5 h-5" />
              <span className="font-inter font-bold text-sm">Download</span>
            </button>
          </div>
        </div>

        {/* Tags */}
        {file.tags && file.tags.length > 0 && (
          <div className="px-4 sm:px-6 pb-4 sm:pb-6 bg-dark-secondary border-t border-white/10">
            <div className="pt-4 sm:pt-6">
              <p className="text-xs text-white/60 font-inter mb-2">Tags</p>
              <div className="flex flex-wrap gap-2">
                {file.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="px-3 py-1 bg-white/10 text-white/80 rounded-full text-xs sm:text-sm font-inter"
                  >
                    {tag.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </>,
    document.body
  );
};

